import {
  CollectionName,
  CollectionVisibility,
} from "../registry/collection-registry"
import type { CollectionCreateSchema } from "../types/typesense"

export const exportCertificatesSchema: CollectionCreateSchema = {
  name: CollectionName.ExportCertificates,
  enable_nested_fields: true,
  fields: [
    {
      name: "party_id",
      type: "string",
      index: false,
    },
    {
      name: "country_id",
      type: "string",
      index: false,
    },
    {
      name: "country_name",
      type: "string",
      facet: true,
      infix: true,
      optional: true,
      sort: true,
    },
    {
      name: "request_name",
      type: "string",
      facet: false,
      infix: true,
      optional: true,
    },
    {
      name: "status",
      type: "string",
      facet: true,
      sort: true,
      infix: true,
      optional: true,
    },
    {
      name: "status_label",
      type: "string",
      facet: true,
      infix: false,
      optional: true,
      sort: true,
    },
    {
      name: "certificate_number",
      type: "string",
      infix: true,
      sort: true,
    },
    {
      name: "certificate_number_sort",
      type: "string",
      optional: true,
      sort: true,
    },
    {
      name: "departure_date",
      type: "string",
      optional: true,
      infix: false,
      index: true,
      sort: true,
    },
    {
      name: "departure_ts",
      type: "int64",
      facet: true,
      optional: true,
      index: true,
      sort: true,
    },
    {
      name: "modified_at",
      type: "string",
      optional: true,
      infix: false,
      sort: true,
      index: true,
    },
    {
      name: "modified_ts",
      type: "int64",
      facet: false,
      optional: true,
      index: true,
      sort: true,
    },
    {
      // Full precision timestamp for accurate "Last modified" sorting
      // (modified_ts is truncated to NZ day, which breaks sort order)
      name: "modified_ts_sort",
      type: "int64",
      optional: true,
      sort: true,
      index: true,
    },
    {
      name: "template_code",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "organisation",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "accessible_by",
      type: "string[]",
      optional: true,
    },
    {
      name: "unofficial_information",
      type: "string",
      optional: true,
      infix: true,
    },
    {
      name: "submitted_at",
      type: "int64",
      optional: true,
      facet: true,
      sort: true,
    },
    {
      name: "approved_at",
      type: "int64",
      optional: true,
      facet: true,
      sort: true,
    },
    {
      name: "approved_date",
      type: "string",
      optional: true,
      infix: true,
    },
    {
      name: "destination_country",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "destination_country_name",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
    },
    {
      name: "unofficial_commercial_info",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "certificate_sub_type",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "verifying_agency",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "certifying_agency",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "external_consignor",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "product",
      type: "object[]",
      infix: true,
      optional: true,
      fields: [
        {
          name: "id",
          type: "string",
          index: false,
        },
        {
          name: "description",
          type: "string",
        },
        {
          name: "product_botanical_name",
          type: "string",
          optional: true,
        },
        {
          name: "product_common_name",
          type: "string",
          infix: true,
          optional: true,
        },
        {
          name: "sub_class",
          type: "string",
          optional: true,
          facet: true,
        },
        {
          name: "class",
          type: "string",
          optional: true,
          facet: true,
        },
        {
          name: "quantity_amount",
          type: "string",
          optional: true,
          infix: true,
          facet: true,
        },
        {
          name: "quantity_type",
          type: "string",
          optional: true,
          infix: true,
          facet: true,
        },
        {
          name: "wine_name",
          type: "string",
          optional: true,
          infix: true,
        },
        {
          name: "net_volume",
          type: "string",
          optional: true,
          index: false,
        },
        {
          name: "package_quantity",
          type: "string",
          optional: true,
          infix: true,
        },
        {
          name: "package_type",
          type: "string",
          optional: true,
          infix: true,
        },
        {
          name: "product_id",
          type: "string",
          optional: true,
          infix: true,
        },
        {
          name: "exporter_reference",
          type: "string",
          optional: true,
          infix: true,
        },
        {
          name: "description",
          type: "string",
          optional: true,
        },
        {
          name: "net_weight",
          type: "string",
          optional: true,
          index: false,
        },
      ],
    },
    {
      name: "product_descriptions",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_net_weights",
      type: "string[]",
      optional: true,
      index: false,
    },
    {
      name: "product_net_volumes",
      type: "string[]",
      optional: true,
      index: false,
    },
    {
      name: "product_eligibility_countries",
      type: "string[]",
      optional: true,
      infix: true,
    },
    {
      name: "product_container_numbers",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_seals",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_NZHSC_codes",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_productionBatch_ids",
      type: "string[]",
      optional: true,
      infix: true,
    },
    {
      name: "product_botanical_name",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "unique_consignment_ref",
      type: "string",
      infix: true,
      optional: true,
    },
    {
      name: "product_item_marks",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_unofficial_commercial_ref",
      type: "string[]",
      optional: true,
      infix: true,
    },
    {
      name: "product_unofficial_commercial_info",
      type: "string[]",
      optional: true,
      infix: true,
    },
    {
      name: "local_carrier",
      type: "string",
      optional: true,
      infix: true,
    },
    {
      name: "carrier_name",
      type: "string",
      optional: true,
      infix: true,
    },
    {
      name: "conveyance_reference",
      type: "string",
      optional: true,
      infix: true,
    },
    {
      name: "template_id",
      type: "string",
      optional: true,
    },
    {
      name: "template_version_id",
      type: "string",
      optional: true,
    },
    {
      name: "template_version",
      type: "int32",
      optional: true,
    },
    {
      name: "submission_layout",
      type: "string",
      optional: true,
    },
    {
      name: "transport_mode",
      type: "string",
      optional: true,
      sort: true,
      infix: true,
      facet: true,
    },
    {
      name: "org_cert_ref_code",
      type: "string",
      optional: true,
      infix: true,
    },
    {
      name: "vessel",
      type: "string",
      optional: true,
      infix: true,
    },
    {
      name: "sub_class",
      type: "string[]",
      optional: true,
      infix: true,
      facet: true,
    },
    {
      name: "class",
      type: "string[]",
      optional: true,
      infix: true,
      facet: true,
    },
    {
      name: "requires_attention",
      type: "bool",
      optional: true,
      index: false,
    },
    {
      name: "consignor_id",
      type: "string",
      facet: true,
      infix: true,
      sort: true,
      optional: true,
    },
    {
      name: "consignee_id",
      type: "string",
      optional: true,
    },
    {
      name: "third_party_consignor",
      type: "string[]",
      infix: true,
      facet: true,
      optional: true,
    },
    {
      name: "commodity",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "certificate_type",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
    },
    {
      name: "destination_market",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
    },
    {
      name: "order_number",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "consignee_name",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "consignee_country",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "exporter_country",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "first_consignee_name",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "exported_by_another_party",
      type: "bool",
      optional: true,
      index: false,
    },
    {
      name: "format_for_shipping",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "total_cases",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "created_at",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "created_ts",
      type: "int64",
      optional: true,
      index: false,
    },
    {
      name: "urgency",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "template_agencies",
      type: "string[]",
      optional: true,
    },
    {
      name: "inspection_port",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "discharge_port",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "importer_name",
      type: "string",
      optional: true,
      facet: false,
      infix: true,
      sort: true,
    },
    {
      name: "importer_id",
      type: "string",
      optional: true,
    },
    {
      name: "inspection_port_country",
      type: "string",
      optional: true,
    },
    {
      name: "oca_access",
      type: "bool",
      optional: true,
      facet: true,
      index: true,
    },
  ],
  visibility: CollectionVisibility.Private,
}
